import React from 'react';
import { 
  BuildingOfficeIcon, 
  Cog6ToothIcon, 
  ChevronRightIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { useAuthBusiness } from '../../contexts/AuthBusinessContext';
import { useNavigate } from 'react-router-dom';
import { isAdminUser } from '../../utils/userUtils';

const BusinessSelectionModal = ({ isOpen, onClose }) => {
  const { memberships, currentUser, currentUserRole, switchBusiness, selectBusiness, isLoading, refreshUserData } = useAuthBusiness();
  const navigate = useNavigate();
  const [showEmptyState, setShowEmptyState] = React.useState(false);
  const [selectingId, setSelectingId] = React.useState(null);

  // 🛡️ LOADING TIMEOUT: Uygulama açılışında auth gecikmesi varsa hemen "yok" dememek için bekle
  React.useEffect(() => {
    let timer;
    if (isOpen && (memberships || []).length === 0) {
      setShowEmptyState(false);
      timer = setTimeout(() => {
        setShowEmptyState(true);
      }, 4000); // 4 saniye bekle (Auth Recovery için süre tanı)
    }
    return () => clearTimeout(timer);
  }, [isOpen, memberships]);

  // 👑 İŞLETME SAHİBİ KONTROLÜ:
  // "Hesap Yönetimi" kartı SADECE owner/admin rollerde görünür. Kasiyer, garson vb. personellere ASLA görünmez.
  const STAFF_ROLES = React.useMemo(() => ['cashier', 'waiter', 'kitchen_staff', 'courier', 'employee', 'staff'], []);

  const isOwnerOrAdmin = React.useMemo(() => {
    if (isAdminUser(currentUser)) return true;

    // currentUserRole en güvenilir kaynak (context'ten geliyor)
    const role = (currentUserRole || '').toLowerCase();

    // Personel rolüyse kesinlikle hayır
    if (STAFF_ROLES.includes(role)) return false;

    // Owner/admin rolü ise evet
    if (role === 'owner' || role === 'business_owner' || role === 'system_admin' || role === 'super_admin') return true;

    // currentUser.role veya user_type fallback
    const fallbackRole = (currentUser?.role || currentUser?.user_type || '').toLowerCase();
    if (STAFF_ROLES.includes(fallbackRole)) return false;
    if (fallbackRole === 'owner' || fallbackRole === 'business_owner' || fallbackRole === 'system_admin') return true;

    // Üyeliklerde owner rolü yoksa hayır (is_business_account tek başına yeterli değil)
    const hasOwnerMembership = (memberships || []).some(m => {
      const mRole = (m.role || '').toLowerCase();
      return mRole === 'owner' || mRole === 'business_owner';
    });
    return hasOwnerMembership;
  }, [currentUser, currentUserRole, memberships, STAFF_ROLES]);

  // 🚀 ZERO-CLICK POS: Eğer kullanıcı işletme sahibi değilse ve yalnızca 1 işletmede kayıtlıysa (örneğin kasiyer),
  // modalda bekletmeden doğrudan o işletmeye yönlendir
  React.useEffect(() => {
    if (!isOpen || isOwnerOrAdmin || selectingId) return;
    const validMemberships = (memberships || []).filter(m => Boolean(m.business_id));
    if (validMemberships.length === 1) {
      console.log('🚀 Single-business staff detected, auto-selecting business for zero-click POS:', validMemberships[0].business_name);
      handleSelect(validMemberships[0]);
    }
  }, [isOpen, isOwnerOrAdmin, memberships, selectingId]);

  // 🛡️ ADMIN SAFETY & OPEN CHECK: Adminler için veya kapalıyken hiçbir şey gösterme
  if (!isOpen || isAdminUser(currentUser) || (isLoading && (memberships || []).length === 0)) {
    // İlk yüklemede ve liste boşken hiçbir şey gösterme veya şık bir loader göster
    if (!isOpen || isAdminUser(currentUser)) return null;
    
    // Loading State (Admin olmayanlar için)
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" />
        <div className="relative bg-white w-full max-w-md rounded-3xl p-12 text-center shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
           <div className="inline-block w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-6"></div>
           <h2 className="text-xl font-black text-slate-900 mb-2">Sistem Hazırlanıyor</h2>
           <p className="text-slate-500 font-medium">Kimliğiniz ve işletmeleriniz doğrulanıyor, lütfen bekleyin...</p>
        </div>
      </div>
    );
  }

  const handleSelect = async (m) => {
    if (selectingId) return; // Çift tıklamayı önle
    const bId = m.business_id;
    setSelectingId(bId);

    const business = {
      id: bId,
      name: m.business_name || 'İşletmem',
      sector: m.sector || 'food-beverage',
      category: m.category,
      category_name: m.category_name || m.category,
      tenant_id: m.tenant_id,
      role: m.role || 'cari'
    };

    // Varsayılan hedef rota (fail-safe fallback)
    const normSector = (business.sector || 'food-beverage').replace(/_/g, '-');
    const isCari = business.role === 'cari' || currentUserRole === 'cari';
    const fallbackPath = isCari 
      ? `/sectors/${normSector}/operations/distribution`
      : (normSector === 'retail' ? '/sectors/retail/dashboard' : '/sectors/food-beverage/dashboard');

    // Seçilen işletmeyi derhal localStorage'a yaz ki sayfa geçişinde DashboardLayout tekrar modal açmasın
    try {
      localStorage.setItem('selectedBusiness', JSON.stringify(business));
      localStorage.setItem('selectedBusinessId', business.id || 'business_account');
    } catch (e) {
      console.warn('localStorage save warning:', e);
    }

    try {
      // 1.5 saniye zaman aşımı ile selectBusiness çağrısı yap (takılmayı engelle)
      const selectPromise = selectBusiness(business);
      const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 1500));
      const targetPath = await Promise.race([selectPromise, timeoutPromise]);

      if (onClose) onClose();
      const finalPath = targetPath || fallbackPath;
      window.location.href = finalPath;
    } catch (err) {
      console.error('İşletme seçim hatası:', err);
      if (onClose) onClose();
      window.location.href = fallbackPath;
    }
  };

  const handleManagement = async () => {
    if (selectingId) return;
    setSelectingId('management');
    try {
      const selectPromise = selectBusiness({ id: 'business_account', name: 'Hesap Yönetimi' });
      const timeoutPromise = new Promise((resolve) => setTimeout(() => resolve(null), 1500));
      const targetPath = await Promise.race([selectPromise, timeoutPromise]);
      if (onClose) onClose();
      window.location.href = targetPath || '/brands';
    } catch (err) {
      console.error('Yönetim seçim hatası:', err);
      if (onClose) onClose();
      window.location.href = '/brands';
    }
  };



  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-md" />

      {/* Modal Content */}
      <div className="relative bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300">
        <div className="p-8 md:p-12">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-black text-slate-900 mb-2">Hoş Geldiniz</h2>
            <p className="text-slate-500 font-medium">Lütfen devam etmek istediğiniz işletmeyi seçin veya yönetim paneline gidin.</p>
          </div>

          <div className="grid gap-4">
            {/* Hesabım / Yönetim opsiyonu - SADECE İşletme Sahibi ve Adminler için */}
            {isOwnerOrAdmin && (
              <button
                type="button"
                disabled={Boolean(selectingId)}
                onClick={handleManagement}
                className={`group flex items-center gap-4 p-5 rounded-2xl border-2 transition-all text-left cursor-pointer touch-manipulation active:scale-[0.98] ${
                  selectingId === 'management'
                    ? 'border-blue-600 bg-blue-50/80 shadow-md ring-2 ring-blue-500/20'
                    : 'bg-slate-50 border-slate-100 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-200 group-hover:bg-blue-600 flex items-center justify-center transition-colors shrink-0">
                  {selectingId === 'management' ? (
                    <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Cog6ToothIcon className="w-6 h-6 text-slate-600 group-hover:text-white" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-black text-slate-900 leading-tight">Hesap Yönetimi</h3>
                    {selectingId === 'management' && (
                      <span className="text-xs font-bold text-blue-600 animate-pulse">Açılıyor...</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-wider mt-0.5">Tüm işletmeleri ve abonelikleri yönetin</p>
                </div>
                <div className="shrink-0">
                  {selectingId === 'management' ? (
                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <ChevronRightIcon className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                  )}
                </div>
              </button>
            )}

            <div className="relative py-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-100"></div>
              </div>
              <div className="relative flex justify-center text-xs uppercase font-black text-slate-400">
                <span className="px-3 bg-white">İşletmelerim</span>
              </div>
            </div>

            <div className="max-h-[350px] overflow-y-auto pr-2 custom-scrollbar grid gap-3">
              {console.log('DEBUG MEMBERSHIPS:', memberships)}
              {(memberships || []).length > 0 ? (
                (memberships || []).map((m) => {
                  const isThisSelecting = selectingId === m.business_id;
                  return (
                    <button
                      key={m.business_id}
                      type="button"
                      disabled={Boolean(selectingId)}
                      onClick={() => handleSelect(m)}
                      className={`group flex items-center gap-4 p-5 rounded-2xl border-2 transition-all text-left cursor-pointer touch-manipulation active:scale-[0.98] ${
                        isThisSelecting 
                          ? 'border-blue-600 bg-blue-50/80 shadow-md ring-2 ring-blue-500/20' 
                          : 'border-slate-100 hover:border-blue-500 hover:bg-blue-50'
                      }`}
                    >
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center overflow-hidden border border-blue-200 shrink-0">
                        {isThisSelecting ? (
                          <div className="w-6 h-6 border-3 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        ) : m.logo ? (
                          <img src={m.logo} alt={m.business_name} className="w-full h-full object-cover" />
                        ) : (
                          <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-black text-slate-900 leading-tight truncate">{m.business_name}</h3>
                          {isThisSelecting && (
                            <span className="text-xs font-bold text-blue-600 animate-pulse">Açılıyor...</span>
                          )}
                        </div>
                        <div className="flex flex-col mt-0.5">
                          <p className="text-[10px] text-blue-600 font-black uppercase tracking-widest leading-none mb-1">
                            {m.displaySector || m.sector}
                          </p>
                          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-tight">
                            {m.category && m.category !== m.sector ? (
                              <>
                                {m.category} • <span className="text-slate-400 font-medium lowercase first-letter:uppercase">{m.displayRole || m.role}</span>
                              </>
                            ) : (
                              <span className="text-slate-400 font-medium lowercase first-letter:uppercase">{m.displayRole || m.role}</span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0">
                        {isThisSelecting ? (
                          <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <ChevronRightIcon className="w-5 h-5 text-slate-300 group-hover:text-blue-500 transition-colors" />
                        )}
                      </div>
                    </button>
                  );
                })
              ) : !showEmptyState ? (
                /* 4 saniyelik bekleme süresi boyunca spinner göster */
                <div className="text-center py-12">
                  <div className="inline-block w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
                  <p className="text-slate-500 font-bold">İşletmeleriniz sorgulanıyor...</p>
                </div>
              ) : (
                <div className="text-center py-10 bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
                  <p className="text-slate-500 font-bold mb-4">Henüz bir işletmeniz bulunmuyor.</p>
                  <div className="flex flex-col sm:flex-row items-center justify-center gap-3 px-4">
                    <button 
                      onClick={refreshUserData}
                      disabled={isLoading}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-white border-2 border-slate-200 text-slate-700 rounded-xl font-black hover:border-blue-500 hover:text-blue-600 transition-all"
                    >
                      {isLoading ? (
                        <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <BuildingOfficeIcon className="w-5 h-5" />
                      )}
                      Yeniden Dene
                    </button>
                    <button 
                      onClick={() => navigate('/dashboard/personal')}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-xl font-black hover:bg-blue-700 transition-all shadow-lg shadow-blue-200"
                    >
                      <PlusIcon className="w-5 h-5" />
                      Hesabıma Git
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default BusinessSelectionModal;
